import axios from 'axios';
import { applyEnvelopeInterceptor } from '@workpay/ds-shared/services/envelopeInterceptor';
import { API_BASE } from './runtimeConfig';

/**
 * Unauthenticated HTTP client for external signing/review flows.
 * No auth headers — all access is token-based.
 *
 * The shared envelope interceptor unwraps the uniform `{ status, data }`
 * response and rejects a normalised WorkpayApiError on failure. No app-level
 * side-effects are wired — external flows are token-scoped and handle
 * auth/expiry per-page (terminal-state screens), not via a global redirect.
 *
 * baseURL is cross-origin to api.<apex> in deployed envs (derived at runtime
 * from window.location.hostname via runtimeConfig). Local dev collapses to
 * the same-origin /api/contract-service path so the Vite proxy / docker
 * stack can serve the requests.
 */
export const publicClient = axios.create({
  baseURL: `${API_BASE}/api/contract-service`,
  headers: { 'Content-Type': 'application/json' },
});

applyEnvelopeInterceptor(publicClient);

/**
 * Platform-service client for the pre-auth workspace-finder email flow.
 * Call paths are `/v1/workspaces/{email-challenge,verify-challenge,resend-challenge}`
 * → full external path `/api/platform-service/v1/workspaces/...`. The gateway
 * strips `/api/platform-service` and its backend target already ends in `/api`,
 * which it re-prepends → the controller's `/api/v1/workspaces/...`. Do NOT add a
 * leading `/api` here: the wrong path isn't in the public-endpoint index, so it
 * misses the public bypass and 401s instead of routing.
 */
export const platformClient = axios.create({
  baseURL: `${API_BASE}/api/platform-service`,
  headers: { 'Content-Type': 'application/json' },
});

applyEnvelopeInterceptor(platformClient);

/**
 * BFF-root client for the pre-auth workspace subdomain probe
 * (GET /workspace-check). This route is registered directly on the BFF router,
 * not proxied under /api/<service>, so its baseURL is the api origin root.
 */
export const bffClient = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

applyEnvelopeInterceptor(bffClient);
